import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { authHeaders } from "../utils/driverPrivacy";
import Plot from "react-plotly.js";
import { chartWatermark } from "../utils/chartTheme";
import "../styles/LapComparisonPage.css";

const API_BASE = "https://iracing6-backend.herokuapp.com/api";

export const LapComparisonPage = () => {
        const location = useLocation();
        const navigate = useNavigate();
        const { lap, session, lap2, session2 } = location.state || {};

        // Determine if we're in "compare 2 selected laps" mode
        const isManualCompare = !!lap2;

        // Determine if this is a "single lap view" (e.g., from contest standings - has lapId but no telemetry data)
        const isSingleLapMode = lap?.lapId && !lap.samples && !isManualCompare;

        const [fastestLaps, setFastestLaps] = useState([]);
        const [selectedLapId, setSelectedLapId] = useState(null);
        const [fastestLap, setFastestLap] = useState(null);
        const [userLapDetails, setUserLapDetails] = useState(null);
        const [loading, setLoading] = useState(true);
        const [loadingLapDetails, setLoadingLapDetails] = useState(false);
        const [loadingUserLap, setLoadingUserLap] = useState(false);
        const [showComparison, setShowComparison] = useState(!isSingleLapMode);
        const [error, setError] = useState(null);
        const [zoomedRange, setZoomedRange] = useState(null);
        const [traceOrder, setTraceOrder] = useState(["speed", "brake", "throttle", "steer"]);
        const [draggedTrace, setDraggedTrace] = useState(null);
        const [dragOverTrace, setDragOverTrace] = useState(null);
        const [sameSeriesOnly, setSameSeriesOnly] = useState(!!session?.seriesId);
        const [sameSessionType, setSameSessionType] = useState(!!session?.sessionType);
        const [timeRange, setTimeRange] = useState("all");

        useEffect(() => {
                if (!lap || !session) {
                        navigate("/telemetry");
                        return;
                }

                // Skip fetching fastest laps if we're comparing 2 user-selected laps or in single lap mode without comparison
                if (!isManualCompare && showComparison) {
                        fetchFastestLaps();
                } else {
                        setLoading(false);
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [sameSeriesOnly, sameSessionType, timeRange, isManualCompare, showComparison]);

        useEffect(() => {
                if (selectedLapId) {
                        fetchLapDetails(selectedLapId);
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedLapId]);

        // Fetch user's lap details if only lapId is provided (e.g., from contest standings)
        useEffect(() => {
                if (lap?.lapId && !lap.speed) {
                        fetchUserLapDetails(lap.lapId);
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lap?.lapId]);

        const fetchUserLapDetails = async (lapId) => {
                setLoadingUserLap(true);
                console.log("Fetching user lap details for lapId:", lapId);
                try {
                        const response = await axios.get(`${API_BASE}/telemetry/lap/${lapId}/optimized`, { headers: authHeaders() });
                        console.log("User lap details response:", response.data);
                        console.log("User lap samples count:", response.data?.optimizedSamples?.samples?.length || response.data?.samples?.length || 0);
                        setUserLapDetails(response.data);
                } catch (err) {
                        console.error("Error fetching user lap details:", err);
                        setError("Could not load lap telemetry data");
                } finally {
                        setLoadingUserLap(false);
                }
        };

        const fetchFastestLaps = async () => {
                setLoading(true);
                setError(null);
                setFastestLap(null);
                setSelectedLapId(null);
                const params = new URLSearchParams();
                params.append("car", session.car);
                params.append("track", session.track);
                params.append("limit", "10");
                if (sameSeriesOnly && session.seriesId) {
                        params.append("seriesId", session.seriesId);
                }
                if (sameSessionType && session.sessionType) {
                        params.append("sessionType", session.sessionType);
                }
                if (timeRange !== "all") {
                        params.append("lastDays", timeRange);
                }

                const url = `${API_BASE}/telemetry/fastest-laps?${params.toString()}`;
                console.log("Fetching fastest laps from:", url);

                try {
                        const response = await axios.get(url, { headers: authHeaders() });
                        console.log("Fastest laps response:", response.data);
                        const laps = response.data.laps || [];
                        setFastestLaps(laps);
                        if (laps.length > 0) {
                                setSelectedLapId(laps[0].lapId);
                        }
                } catch (err) {
                        console.error("Fastest laps error:", err);
                        setError(err.response?.data?.message || err.message);
                } finally {
                        setLoading(false);
                }
        };

        const fetchLapDetails = async (lapId, tryNextOnFail = true) => {
                setLoadingLapDetails(true);
                setError(null);
                try {
                        const response = await axios.get(`${API_BASE}/telemetry/lap/${lapId}/optimized`, { headers: authHeaders() });
                        console.log("Lap details response:", response.data);
                        setFastestLap(response.data);
                } catch (err) {
                        console.error("Lap details error:", err);
                        // If 404 and we should try next, find the next lap in the list
                        if (err.response?.status === 404 && tryNextOnFail && fastestLaps.length > 1) {
                                const currentIndex = fastestLaps.findIndex(l => l.lapId === lapId);
                                const nextLap = fastestLaps[currentIndex + 1];
                                if (nextLap) {
                                        console.log("Lap data not available, trying next lap...");
                                        setSelectedLapId(nextLap.lapId);
                                        return; // useEffect will handle fetching the next lap
                                }
                        }
                        setFastestLap(null);
                        if (err.response?.status === 404) {
                                setError("Detailed telemetry data not available for this lap. Try selecting a different lap.");
                        } else {
                                setError(err.response?.data?.message || err.message);
                        }
                } finally {
                        setLoadingLapDetails(false);
                }
        };

        const formatLapTime = (seconds) => {
                if (!seconds) return "--";
                const mins = Math.floor(seconds / 60);
                const secs = (seconds % 60).toFixed(3);
                return mins > 0 ? `${mins}:${secs.padStart(6, "0")}` : `${secs}s`;
        };

        if (!lap || !session) {
                return null;
        }

        // Use fetched lap details if available (for contest standings clicks)
        const effectiveLap = userLapDetails ? {
                ...lap,
                samples: userLapDetails.optimizedSamples?.samples || userLapDetails.samples || lap.samples,
                lapTime: userLapDetails.lapTime || lap.lapTime,
                maxSpeed: userLapDetails.optimizedSamples?.maxSpeed || userLapDetails.maxSpeed || lap.maxSpeed,
                fullThrottlePercentage: userLapDetails.optimizedSamples?.fullThrottlePercentage || userLapDetails.fullThrottlePercentage || lap.fullThrottlePercentage,
        } : lap;

        // Merge session with conditions from userLapDetails if available
        const effectiveSession = userLapDetails?.session ? {
                ...session,
                conditions: userLapDetails.session.conditions || session?.conditions,
                sessionType: userLapDetails.session.sessionType || session?.sessionType,
                driverName: session?.driverName || userLapDetails.session.driver?.name,
        } : session;

        // Debug: Log effective lap data
        console.log("effectiveLap samples count:", effectiveLap.samples?.length || 0, "userLapDetails:", !!userLapDetails);

        if (loadingUserLap) {
                return (
                        <div className="lap-comparison-page">
                                <div className="loading">Loading lap telemetry data...</div>
                        </div>
                );
        }

        return (
                <div className="lap-comparison-page">
                        <button className="back-button" onClick={() => navigate("/telemetry")}>
                                &larr; Back to Telemetry
                        </button>

                        <h1>{!showComparison ? 'Lap Analysis' : 'Lap Comparison'}</h1>
                        <p className="combo-info">
                                {isManualCompare
                                        ? `${session.car} @ ${session.track}${session2 && (session2.car !== session.car || session2.track !== session.track) ? ` vs ${session2.car} @ ${session2.track}` : ''}`
                                        : `${session.car} @ ${session.track}`}
                        </p>

                        {!showComparison && (
                                <button
                                        className="compare-to-fastest-button"
                                        onClick={() => setShowComparison(true)}
                                        style={{
                                                padding: "0.6rem 1.2rem",
                                                fontSize: "0.95rem",
                                                backgroundColor: "#f5ce00",
                                                color: "#1e1d20",
                                                border: "none",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontWeight: "600",
                                                marginBottom: "1rem",
                                        }}
                                >
                                        Compare to Fastest →
                                </button>
                        )}

                        <div className="comparison-container">
                                <div className="lap-panel your-lap">
                                        <h2>{isManualCompare ? 'Lap 1' : 'Your Lap'}</h2>
                                        <div className="lap-details">
                                                <div className="lap-time">{formatLapTime(lap.lapTime)}</div>
                                                <div className="lap-meta-inline">
                                                        <span>{effectiveSession.driverName}</span>
                                                        <span>Lap #{lap.lapNumber}</span>
                                                        {effectiveSession.sessionType && <span>{effectiveSession.sessionType}</span>}
                                                </div>
                                        </div>
                                </div>

                                {isManualCompare ? (
                                        // Manual compare mode - show lap 2 directly
                                        <div className="lap-panel fastest-lap manual-compare-lap">
                                                <h2>Lap 2</h2>
                                                <div className="lap-details">
                                                        <div className="lap-time">{formatLapTime(lap2.lapTime)}</div>
                                                        <div className="lap-meta-inline">
                                                                <span>{session2.driverName}</span>
                                                                <span>Lap #{lap2.lapNumber}</span>
                                                                {session2.sessionType && <span>{session2.sessionType}</span>}
                                                        </div>
                                                </div>
                                        </div>
                                ) : showComparison ? (
                                        // Fastest lap mode - show filters and lap selector
                                        <div className="fastest-lap-column">
                                                <div className="fastest-lap-filters">
                                                        <label className="filter-checkbox">
                                                                <input type="checkbox" checked={sameSeriesOnly} onChange={(e) => setSameSeriesOnly(e.target.checked)} disabled={!session?.seriesId} />
                                                                Same Series
                                                        </label>
                                                        <label className="filter-checkbox">
                                                                <input type="checkbox" checked={sameSessionType} onChange={(e) => setSameSessionType(e.target.checked)} disabled={!session?.sessionType} />
                                                                Same Session Type
                                                        </label>
                                                        <div className="filter-select">
                                                                <label>Time Range:</label>
                                                                <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                                                                        <option value="7">Last 7 Days</option>
                                                                        <option value="14">Last 14 Days</option>
                                                                        <option value="30">Last 30 Days</option>
                                                                        <option value="all">All Time</option>
                                                                </select>
                                                        </div>
                                                </div>
                                                {fastestLaps.length > 0 && (
                                                        <div className="lap-selector">
                                                                <label>Top 10 Laps:</label>
                                                                <select value={selectedLapId || ""} onChange={(e) => setSelectedLapId(e.target.value)} disabled={loading || loadingLapDetails}>
                                                                        {fastestLaps.map((lapOption, index) => (
                                                                                <option key={lapOption.lapId} value={lapOption.lapId}>
                                                                                        {index + 1}. {formatLapTime(lapOption.lapTime)} - {lapOption.session?.driver?.name || "Unknown"} (
                                                                                        {lapOption.session?.sessionType || "N/A"})
                                                                                </option>
                                                                        ))}
                                                                </select>
                                                        </div>
                                                )}
                                                <div className="lap-panel fastest-lap">
                                                        <h2>Fastest Lap {timeRange === "all" ? "(All Time)" : `(Last ${timeRange} Days)`}</h2>
                                                        {loading || loadingLapDetails ? (
                                                                <div className="loading">Loading lap data...</div>
                                                        ) : error ? (
                                                                <div className="panel-error">{error}</div>
                                                        ) : fastestLap ? (
                                                                <div className="lap-details">
                                                                        <div className="lap-time">{formatLapTime(fastestLap.lapTime || fastestLaps.find((l) => l.lapId === selectedLapId)?.lapTime)}</div>
                                                                        <div className="lap-meta-inline">
                                                                                <span>{fastestLap.session?.driver?.name}</span>
                                                                                <span>Lap #{fastestLap.lapNumber || fastestLaps.find((l) => l.lapId === selectedLapId)?.lapNumber}</span>
                                                                                {fastestLap.session?.sessionType && <span>{fastestLap.session.sessionType}</span>}
                                                                        </div>
                                                                </div>
                                                        ) : (
                                                                <div className="no-data">No fastest lap found for this combo</div>
                                                        )}
                                                </div>
                                        </div>
                                ) : null}
                        </div>

                        {(() => {
                                const yourSamples = effectiveLap.samples || [];
                                const fastestSamples = isManualCompare
                                        ? (lap2?.samples || [])
                                        : showComparison ? (fastestLap?.optimizedSamples?.samples || []) : [];

                                return (
                                        <div className="track-map-row">
                                                <div className="track-map-container">
                                                        <div className="trace-header">
                                                                <h2>Racing Line</h2>
                                                                <p className="chart-hint">drag to zoom / double click to reset</p>
                                                        </div>
                                                        {loading ? <div className="loading">Loading...</div> : <TrackMap2D yourSamples={yourSamples} fastestSamples={fastestSamples} isManualCompare={isManualCompare} />}
                                                </div>
                                                <div className="track-map-sidebar">
                                                        <div className="trace-header">
                                                                <h2>{showComparison ? 'Stats Comparison' : 'Lap Stats'}</h2>
                                                        </div>
                                                        {loading ? <div className="loading">Loading...</div> : <StatsComparison lap={effectiveLap} fastestLap={isManualCompare ? { optimizedSamples: lap2, session: session2 } : (showComparison ? fastestLap : null)} session={effectiveSession} isManualCompare={isManualCompare} />}
                                                </div>
                                        </div>
                                );
                        })()}

                        {(() => {
                                const yourSamples = effectiveLap.samples || [];
                                const fastestSamples = isManualCompare
                                        ? (lap2?.samples || [])
                                        : showComparison ? (fastestLap?.optimizedSamples?.samples || []) : [];
                                const allX = [...yourSamples.map((s) => s.dist ?? s.lapDist ?? 0), ...fastestSamples.map((s) => s.dist ?? s.lapDist ?? 0)];
                                const defaultRange = allX.length > 0 ? [Math.min(...allX), Math.max(...allX)] : [0, 1];
                                const xRange = zoomedRange || defaultRange;

                                const handleZoom = (e) => {
                                        if (e["xaxis.range[0]"] !== undefined && e["xaxis.range[1]"] !== undefined) {
                                                setZoomedRange([e["xaxis.range[0]"], e["xaxis.range[1]"]]);
                                        } else if (e["xaxis.autorange"]) {
                                                setZoomedRange(null);
                                        }
                                };

                                const traceLabels = {
                                        speed: "Speed",
                                        throttle: "Throttle",
                                        brake: "Brake",
                                        steer: "Steering",
                                };

                                const handleDragStart = (e, traceKey) => {
                                        setDraggedTrace(traceKey);
                                        e.dataTransfer.effectAllowed = "move";
                                };

                                const handleDragEnter = (e, traceKey) => {
                                        e.preventDefault();
                                        if (draggedTrace && draggedTrace !== traceKey) {
                                                setDragOverTrace(traceKey);
                                        }
                                };

                                const handleDragOver = (e, traceKey) => {
                                        e.preventDefault();
                                        if (draggedTrace && draggedTrace !== traceKey) {
                                                const newOrder = [...traceOrder];
                                                const draggedIndex = newOrder.indexOf(draggedTrace);
                                                const targetIndex = newOrder.indexOf(traceKey);
                                                newOrder.splice(draggedIndex, 1);
                                                newOrder.splice(targetIndex, 0, draggedTrace);
                                                setTraceOrder(newOrder);
                                        }
                                };

                                const handleDragLeave = () => {
                                        setDragOverTrace(null);
                                };

                                const handleDragEnd = () => {
                                        setDraggedTrace(null);
                                        setDragOverTrace(null);
                                };

                                return (
                                        <div className="traces-container">
                                                {traceOrder.map((traceKey) => (
                                                        <div
                                                                key={traceKey}
                                                                className={`combined-trace-container ${draggedTrace === traceKey ? "dragging" : ""} ${dragOverTrace === traceKey ? "drag-over" : ""}`}
                                                                draggable
                                                                onDragStart={(e) => handleDragStart(e, traceKey)}
                                                                onDragEnter={(e) => handleDragEnter(e, traceKey)}
                                                                onDragOver={(e) => handleDragOver(e, traceKey)}
                                                                onDragLeave={handleDragLeave}
                                                                onDragEnd={handleDragEnd}
                                                        >
                                                                <div className="trace-header">
                                                                        <span className="drag-handle">⋮⋮</span>
                                                                        <h2>{traceLabels[traceKey]} Trace</h2>
                                                                        <p className="chart-hint">drag to zoom / double click to reset</p>
                                                                </div>
                                                                {loading ? (
                                                                        <div className="loading">Loading...</div>
                                                                ) : (
                                                                        <CombinedTelemetryTrace
                                                                                yourSamples={yourSamples}
                                                                                fastestSamples={fastestSamples}
                                                                                dataKey={traceKey}
                                                                                xRange={xRange}
                                                                                onZoom={handleZoom}
                                                                                isManualCompare={isManualCompare}
                                                                        />
                                                                )}
                                                        </div>
                                                ))}
                                        </div>
                                );
                        })()}

                        {(() => {
                                const compareLapTime = isManualCompare ? lap2?.lapTime : fastestLap?.lapTime;
                                if (!lap.lapTime || !compareLapTime) return null;

                                const isFaster = lap.lapTime <= compareLapTime;
                                const deltaLabel = isManualCompare
                                        ? (isFaster ? "Lap 1 is faster" : "Lap 2 is faster")
                                        : (isFaster ? "You're faster!" : "Gap to fastest");

                                return (
                                        <div className="time-delta">
                                                <span className={isFaster ? "faster" : "slower"}>
                                                        {isFaster ? "-" : "+"}
                                                        {Math.abs(lap.lapTime - compareLapTime).toFixed(3)}s
                                                </span>
                                                <span className="delta-label">{deltaLabel}</span>
                                        </div>
                                );
                        })()}
                </div>
        );
};

const CombinedTelemetryTrace = ({ yourSamples, fastestSamples, dataKey = "brake", xRange, onZoom, isManualCompare }) => {
        const hasYourData = yourSamples && yourSamples.length > 0;
        const hasFastestData = fastestSamples && fastestSamples.length > 0;

        if (!hasYourData && !hasFastestData) {
                return <div className="no-trace">No telemetry data available</div>;
        }

        const isPercentage = dataKey === "brake" || dataKey === "throttle";
        const getValue = (s) => {
                if (dataKey === "brake") {
                        return (s.brake ?? 0) * 100;
                }
                if (dataKey === "throttle") {
                        return (s.throttle ?? 0) * 100;
                }
                if (dataKey === "steer") {
                        return s.steer ?? 0;
                }
                return s.speed ?? 0;
        };

        const getYAxisLabel = () => {
                if (dataKey === "brake") return "Brake %";
                if (dataKey === "throttle") return "Throttle %";
                if (dataKey === "steer") return "Steering";
                return "Speed";
        };

        const traces = [];

        if (hasFastestData) {
                traces.push({
                        x: fastestSamples.map((s) => s.dist ?? s.lapDist ?? 0),
                        y: fastestSamples.map(getValue),
                        type: "scatter",
                        mode: "lines",
                        name: isManualCompare ? "Lap 2" : "Fastest Lap",
                        line: { color: "#f5ce00", width: 2 },
                        opacity: 0.8,
                });
        }

        if (hasYourData) {
                traces.push({
                        x: yourSamples.map((s) => s.dist ?? s.lapDist ?? 0),
                        y: yourSamples.map(getValue),
                        type: "scatter",
                        mode: "lines",
                        name: isManualCompare ? "Lap 1" : "Your Lap",
                        line: { color: "#3b82f6", width: 2 },
                });
        }

        const layout = {
                autosize: true,
                height: 320,
                margin: { l: 60, r: 30, t: 40, b: 45 },
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                xaxis: {
                        title: {
                                text: "Track Distance",
                                font: { color: "#cfcfcf" },
                        },
                        showgrid: true,
                        zeroline: true,
                        gridcolor: "#555",
                        zerolinecolor: "#555",
                        linecolor: "#cfcfcf",
                        tickfont: { color: "#cfcfcf" },
                        range: xRange,
                },
                yaxis: {
                        title: {
                                text: getYAxisLabel(),
                                font: { color: "#cfcfcf" },
                        },
                        showgrid: true,
                        zeroline: true,
                        gridcolor: "#555",
                        zerolinecolor: "#555",
                        linecolor: "#cfcfcf",
                        tickfont: { color: "#cfcfcf" },
                        ...(isPercentage ? { range: [0, 100], ticksuffix: "%" } : {}),
                },
                legend: {
                        font: { color: "#cfcfcf" },
                        bgcolor: "transparent",
                        orientation: "h",
                        y: 1.1,
                },
                font: {
                        color: "#cfcfcf",
                        family: "Questrial, sans-serif",
                },
                dragmode: "zoom",
                hovermode: false,
                annotations: [chartWatermark("top-right", { outside: true })],
        };

        const config = {
                responsive: true,
                displayModeBar: false,
                scrollZoom: false,
        };

        return <Plot data={traces} layout={layout} config={config} style={{ width: "100%" }} useResizeHandler={true} onRelayout={onZoom} />;
};

const TrackMap2D = ({ yourSamples, fastestSamples, isManualCompare }) => {
        const hasYourData = yourSamples && yourSamples.length > 0;
        const hasFastestData = fastestSamples && fastestSamples.length > 0;

        if (!hasYourData && !hasFastestData) {
                return <div className="no-trace">No GPS data available</div>;
        }

        // Check if we have lat/long data
        const yourHasGps = hasYourData && yourSamples.some((s) => s.lat != null && s.lon != null);
        const fastestHasGps = hasFastestData && fastestSamples.some((s) => s.lat != null && s.lon != null);

        if (!yourHasGps && !fastestHasGps) {
                return <div className="no-trace">No GPS coordinates in telemetry data</div>;
        }

        // Use whichever samples have GPS data for distance markers
        const samplesForMarkers = yourHasGps ? yourSamples : fastestSamples;
        const gpsFilteredSamples = samplesForMarkers.filter((s) => s.lat != null && s.lon != null);

        // Find samples at 0.1, 0.2, 0.3, etc. distance intervals
        const getDistanceMarkers = () => {
                if (gpsFilteredSamples.length === 0) return { x: [], y: [], text: [] };

                const maxDist = Math.max(...gpsFilteredSamples.map((s) => s.dist ?? s.lapDist ?? 0));
                const minDist = Math.min(...gpsFilteredSamples.map((s) => s.dist ?? s.lapDist ?? 0));

                // Determine interval based on data range
                let interval, formatLabel;
                if (maxDist <= 1.5) {
                        // Lap fraction (0-1 range)
                        interval = 0.1;
                        formatLabel = (v) => v.toFixed(1);
                } else if (maxDist <= 10) {
                        // Kilometers
                        interval = 1;
                        formatLabel = (v) => v.toFixed(0);
                } else {
                        // Meters - use 500m or 1000m intervals
                        interval = maxDist > 5000 ? 1000 : 500;
                        formatLabel = (v) => (v / 1000).toFixed(1) + "k";
                }

                const markerPoints = { x: [], y: [], text: [] };

                for (let targetDist = interval; targetDist < maxDist; targetDist += interval) {
                        // Find the sample closest to this distance
                        let closest = null;
                        let closestDiff = Infinity;

                        for (const sample of gpsFilteredSamples) {
                                const dist = sample.dist ?? sample.lapDist ?? 0;
                                const diff = Math.abs(dist - targetDist);
                                if (diff < closestDiff) {
                                        closestDiff = diff;
                                        closest = sample;
                                }
                        }

                        if (closest && closestDiff < interval * 0.5) {
                                markerPoints.x.push(closest.lon);
                                markerPoints.y.push(closest.lat);
                                markerPoints.text.push(formatLabel(targetDist));
                        }
                }

                return markerPoints;
        };

        const distanceMarkers = getDistanceMarkers();

        const traces = [];

        if (fastestHasGps) {
                traces.push({
                        x: fastestSamples.filter((s) => s.lon != null).map((s) => s.lon),
                        y: fastestSamples.filter((s) => s.lat != null).map((s) => s.lat),
                        type: "scatter",
                        mode: "lines",
                        name: isManualCompare ? "Lap 2" : "Fastest Lap",
                        line: { color: "#f5ce00", width: 3 },
                        opacity: 0.8,
                });
        }

        if (yourHasGps) {
                traces.push({
                        x: yourSamples.filter((s) => s.lon != null).map((s) => s.lon),
                        y: yourSamples.filter((s) => s.lat != null).map((s) => s.lat),
                        type: "scatter",
                        mode: "lines",
                        name: isManualCompare ? "Lap 1" : "Your Lap",
                        line: { color: "#3b82f6", width: 3 },
                });
        }

        // Add distance markers
        if (distanceMarkers.x.length > 0) {
                traces.push({
                        x: distanceMarkers.x,
                        y: distanceMarkers.y,
                        text: distanceMarkers.text,
                        type: "scatter",
                        mode: "markers+text",
                        name: "Distance",
                        marker: { color: "#fff", size: 6 },
                        textfont: { color: "#fff", size: 14 },
                        textposition: "top center",
                        showlegend: false,
                });
        }

        const layout = {
                autosize: true,
                height: 280,
                margin: { l: 10, r: 10, t: 30, b: 10 },
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                xaxis: {
                        showgrid: false,
                        showline: false,
                        zeroline: false,
                        showticklabels: false,
                        scaleanchor: "y",
                        scaleratio: 1,
                },
                yaxis: {
                        showgrid: false,
                        showline: false,
                        zeroline: false,
                        showticklabels: false,
                },
                legend: {
                        font: { color: "#cfcfcf" },
                        bgcolor: "transparent",
                        orientation: "h",
                        y: 1.08,
                },
                font: {
                        color: "#cfcfcf",
                        family: "Questrial, sans-serif",
                },
                dragmode: "zoom",
                hovermode: false,
                annotations: [chartWatermark("top-right", { outside: true })],
        };

        const config = {
                responsive: true,
                displayModeBar: false,
                scrollZoom: false,
        };

        return <Plot data={traces} layout={layout} config={config} style={{ width: "100%", height: "280px" }} useResizeHandler={true} />;
};

const StatsComparison = ({ lap, fastestLap, session, isManualCompare }) => {
        const yourStats = {
                maxSpeed: lap?.maxSpeed,
                fullThrottle: lap?.fullThrottlePercentage,
                trackTemp: session?.conditions?.trackTemp,
                airTemp: session?.conditions?.airTemp,
        };

        const fastestStats = {
                maxSpeed: fastestLap?.optimizedSamples?.maxSpeed,
                fullThrottle: fastestLap?.optimizedSamples?.fullThrottlePercentage,
                trackTemp: fastestLap?.session?.conditions?.trackTemp,
                airTemp: fastestLap?.session?.conditions?.airTemp,
        };

        const formatValue = (value, decimals = 1, suffix = "") => {
                if (value == null) return "--";
                return value.toFixed(decimals) + suffix;
        };

        const stats = [
                {
                        label: "Max Speed",
                        yours: formatValue(yourStats.maxSpeed, 1),
                        fastest: formatValue(fastestStats.maxSpeed, 1),
                },
                {
                        label: "Full Throttle",
                        yours: formatValue(yourStats.fullThrottle, 1, "%"),
                        fastest: formatValue(fastestStats.fullThrottle, 1, "%"),
                },
                {
                        label: "Track Temp",
                        yours: yourStats.trackTemp != null ? `${yourStats.trackTemp}°C` : "--",
                        fastest: fastestStats.trackTemp != null ? `${fastestStats.trackTemp}°C` : "--",
                },
                {
                        label: "Air Temp",
                        yours: yourStats.airTemp != null ? `${yourStats.airTemp}°C` : "--",
                        fastest: fastestStats.airTemp != null ? `${fastestStats.airTemp}°C` : "--",
                },
        ];

        return (
                <div className="stats-comparison">
                        <div className="stats-header">
                                <span className="stat-label"></span>
                                <span className="stat-yours">{isManualCompare ? 'Lap 1' : 'You'}</span>
                                <span className="stat-fastest">{isManualCompare ? 'Lap 2' : 'Fastest'}</span>
                        </div>
                        {stats.map((stat) => (
                                <div key={stat.label} className="stats-row">
                                        <span className="stat-label">{stat.label}</span>
                                        <span className="stat-yours">{stat.yours}</span>
                                        <span className="stat-fastest">{stat.fastest}</span>
                                </div>
                        ))}
                </div>
        );
};

export default LapComparisonPage;
